import { useEffect, useMemo, useRef, useState, type ChangeEvent } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import TopBarHeader from '@/domains/mobile-web/components/TopBarHeader/TopBarHeader';
import { useCartCount } from '@/domains/mobile-web/hooks/useCart';
import HeaderMobile from '@/shared/components/ui/HeaderMobile/HeaderMobile';
import WrapperMobile from '@/shared/components/layout/WrapperMobile/WrapperMobile';
import PostComposer, { ComposerActionBar } from '@/domains/mobile-web/components/PostComposer/PostComposer';
import { useCreateBookRequest } from '@/domains/mobile-web/hooks/useBookRequests';
import { useComposerUser } from '@/domains/mobile-web/hooks/useComposerUser';
import { useCreateKidsPost } from '@/domains/mobile-web/hooks/useKidsPosts';
import { showToast } from '@/shared/utils/showToast';

type PostLocationState = { from?: 'donation' | 'media' };

// Post text (Donasi) and caption (Media) limit (characters, after trimming)
const MAX_POST_LENGTH = 2000;
// Photo forum limits, checked again by the server
const MAX_PHOTOS = 5;
const MAX_PHOTO_BYTES = 10 * 1024 * 1024;
const PHOTO_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

const Post = () => {
    const navigate = useNavigate();
    const cartCount = useCartCount();
    const location = useLocation();
    const state = location.state as PostLocationState | null;
    const isMedia = (state?.from ?? 'donation') === 'media';
    const backPath = isMedia ? '/store/media' : '/store/donation';
    const composerUser = useComposerUser();
    const createBookRequest = useCreateBookRequest();
    const createKidsPost = useCreateKidsPost();
    const isSubmitting = createBookRequest.isPending || createKidsPost.isPending;

    const [content, setContent] = useState('');
    const [mediaFiles, setMediaFiles] = useState<File[]>([]);
    const fileInputRef = useRef<HTMLInputElement>(null);

    // object URL untuk preview; dibersihkan saat file berubah / unmount
    const mediaPreviews = useMemo(() => mediaFiles.map((file) => URL.createObjectURL(file)), [mediaFiles]);
    useEffect(() => () => mediaPreviews.forEach((url) => URL.revokeObjectURL(url)), [mediaPreviews]);

    const handleGallery = () => fileInputRef.current?.click();

    const handleFilesSelected = (e: ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(e.target.files ?? []);
        e.target.value = '';
        const usable = files.filter(
            (file) => PHOTO_TYPES.includes(file.type) && file.size > 0 && file.size <= MAX_PHOTO_BYTES
        );
        if (usable.length < files.length) {
            showToast({
                type: 'error',
                title: 'Foto tidak dapat dipakai',
                message: 'Foto harus berformat JPG, PNG atau WebP, maksimal 10 MB.',
            });
        }
        const next = [...mediaFiles, ...usable];
        if (next.length > MAX_PHOTOS) {
            showToast({
                type: 'error',
                title: 'Terlalu banyak foto',
                message: `Maksimal ${MAX_PHOTOS} foto per postingan.`,
            });
        }
        if (usable.length > 0) setMediaFiles(next.slice(0, MAX_PHOTOS));
    };

    const handleRemoveMedia = (index: number) =>
        setMediaFiles((prev) => prev.filter((_, i) => i !== index));

    const handleSubmit = () => {
        if (isMedia) {
            if (createKidsPost.isPending) return;
            if (mediaFiles.length === 0) {
                showToast({ type: 'error', title: 'Belum ada foto', message: 'Pilih minimal satu foto.' });
                return;
            }
            createKidsPost.mutate(
                { caption: content.trim(), photos: mediaFiles },
                { onSuccess: () => navigate(backPath) }
            );
            return;
        }
        // A donation post is text only
        if (!content.trim() || createBookRequest.isPending) return;
        createBookRequest.mutate(content.trim(), { onSuccess: () => navigate(backPath) });
    };

    return (
        <main className="min-h-screen">
            <HeaderMobile>
                <TopBarHeader
                    type="textBucket"
                    totalProduct={cartCount}
                    title={isMedia ? 'Media (Foto Anak)' : 'Donasi'}
                    onAction={() => navigate('/store/cart')}
                />
                <ComposerActionBar
                    disabled={isSubmitting}
                    onGallery={isMedia ? handleGallery : undefined}
                    onCancel={() => navigate(-1)}
                    onSubmit={handleSubmit}
                />
            </HeaderMobile>

            {isMedia && (
                <input
                    ref={fileInputRef}
                    type="file"
                    accept={PHOTO_TYPES.join(',')}
                    multiple
                    hidden
                    onChange={handleFilesSelected}
                />
            )}

            <WrapperMobile className="pt-3">
                <PostComposer
                    user={composerUser}
                    value={content}
                    onChange={setContent}
                    maxLength={MAX_POST_LENGTH}
                    placeholder={isMedia ? 'Ceritakan foto anda..' : 'Deskripsikan buku yang anda inginkan..'}
                    media={mediaPreviews}
                    onRemoveMedia={handleRemoveMedia}
                />
            </WrapperMobile>
        </main>
    );
};

export default Post;
